import { fromJS, Map } from 'immutable';

import baseReducer from 'redux/baseReducer';
import { createCollectionSymbols, createResourceSymbols } from 'utils/helpers';

export function collectionReducer(
    { namespace, transformer = (item) => item },
    reducerOrReducerFunc
) {
    const types = createCollectionSymbols(namespace);
    const {
        DELETE_SUCCESS,
        EDIT_IN,
        FETCH,
        FETCH_FAIL,
        FETCH_SUCCESS,
        POST_SUCCESS,
        RESET,
    } = types;
    const initialState = fromJS({
        cache: {},
        data: [],
        hasFetched: false,
        isFetching: false,
        lastFetched: null,
        pagination: {},
    });
    const reducer = typeof reducerOrReducerFunc === 'function'
        ? reducerOrReducerFunc(types)
        : reducerOrReducerFunc;

    const deleteSuccess = (state, { meta }) => {
        return state.set('data', state.get('data').filter((item) => {
            return item.get('id') !== meta.previousAction.data.get('id');
        }));
    };
    const editInState = (state, { path, value }) => state.setIn(path, value);
    const fetch = (state) => state.set('isFetching', true);
    const fetchFail = (state) => state.set('isFetching', false);
    const fetchSuccess = (state, { meta, payload }) => state.withMutations(
        (prevState) => {
            const prevData = prevState.get('data') ? prevState.get('data') : [];
            const data = payload.data.data || payload.data;
            let extendedData = !Array.isArray(data) ? [data] : data;

            if (meta.previousAction.payload.request.params.extend) {
                extendedData = [...prevData.toJS(), ...extendedData];
            }

            const transformedData = fromJS(extendedData).map(transformer);
            const cache = prevState.get('cache');
            const updatedCache = cache.set(meta.previousAction.requestId, Map({
                data: transformedData,
                lastFetched: Date.now(),
            }));

            prevState.set('hasFetched', true)
                .set('isFetching', false)
                .set('pagination', fromJS(payload.pagination))
                .set('data', transformedData)
                .set('cache', updatedCache);
        }
    );
    const postSuccess = (state, { payload }) => {
        return state.set(
            'data',
            state.get('data').push(
                transformer(fromJS(payload.data))
            )
        );
    };
    const reset = () => initialState;

    return {
        reducer: baseReducer(initialState, {
            [DELETE_SUCCESS]: deleteSuccess,
            [EDIT_IN]: editInState,
            [FETCH]: fetch,
            [FETCH_FAIL]: fetchFail,
            [FETCH_SUCCESS]: fetchSuccess,
            [POST_SUCCESS]: postSuccess,
            [RESET]: reset,
            ...reducer,
        }),
        types,
    };
}

export function resourceReducer({ namespace, transformer = (item) => item }, reducer) {
    const types = createResourceSymbols(namespace);
    const {
        EDIT_IN,
        FETCH,
        FETCH_FAIL,
        FETCH_SUCCESS,
        PATCH,
        PATCH_FAIL,
        PATCH_SUCCESS,
        PUT,
        PUT_FAIL,
        PUT_SUCCESS,
        RESET,
    } = types;
    const initialState = fromJS({
        cache: {},
        data: transformer({}),
        hasFetched: false,
        isFetching: false,
        lastFetched: null,
    });

    const editInState = (state, { path, value }) => state.setIn(path, value);
    const fetchResource = (state) => state.set('isFetching', true);
    const fetchResourceFail = (state) => state.set('isFetching', false);
    const fetchResourceSuccess = (state, { payload }) => state.withMutations(
        (prevState) => {
            return prevState
                .set('hasFetched', true)
                .set('isFetching', false)
                .set('lastFetched', Date.now())
                .set('data', transformer(
                    fromJS(payload.data)
                ));
        }
    );
    const putResource = (state) => state.set('isFetching', true);
    const putResourceFail = (state) => state.set('isFetching', false);
    const putResourceSuccess = (state, { payload }) => state.withMutations(
        (prevState) => prevState
            .set('hasFetched', true)
            .set('isFetching', false)
            .set('lastFetched', Date.now())
            .set('data', transformer(
                fromJS(payload.data)
            ))
    );
    const reset = () => initialState;

    return {
        reducer: baseReducer(initialState, {
            [EDIT_IN]: editInState,
            [FETCH]: fetchResource,
            [FETCH_FAIL]: fetchResourceFail,
            [FETCH_SUCCESS]: fetchResourceSuccess,
            [PATCH]: putResource,
            [PATCH_FAIL]: putResourceFail,
            [PATCH_SUCCESS]: putResourceSuccess,
            [PUT]: putResource,
            [PUT_FAIL]: putResourceFail,
            [PUT_SUCCESS]: putResourceSuccess,
            [RESET]: reset,
            ...reducer,
        }),
        types,
    };
}
